import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon, { HelpHint } from '../common/SafeIcon';
import {
  FiPlus, FiSearch, FiEdit3, FiEye, FiX, FiRefreshCw,
  FiMail, FiKey, FiBox, FiActivity, FiSave, FiAlertCircle
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const blankForm = {
  id: '',
  name: '',
  contactEmail: '',
  industry: '',
  status: 'active',
  monthlyLimit: 0,
  annualLimit: 0,
  includedEmailLimit: 0,
  fromName: '',
  fromEmail: '',
  replyToEmail: '',
  notes: ''
};

const numberValue = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const Clients = () => {
  const navigate=useNavigate();
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const loadClients = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await gatewayApi.listClients();
      setClients(response.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load clients.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const filteredClients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(client =>
      [client.name, client.contactEmail, client.industry, client.status]
        .some(value => String(value || '').toLowerCase().includes(term))
    );
  }, [clients, searchTerm]);

  const openCreate = () => {
    setForm(blankForm);
    setEditorOpen(true);
  };

  const openEdit = (client) => {
    setForm({
      id: client.id,
      name: client.name || '',
      contactEmail: client.contactEmail || '',
      industry: client.industry || '',
      status: client.status || 'active',
      monthlyLimit: client.monthlyLimit || 0,
      annualLimit: client.annualLimit || 0,
      includedEmailLimit: client.includedEmailLimit || 0,
      fromName: client.fromName || '',
      fromEmail: client.fromEmail || '',
      replyToEmail: client.replyToEmail || '',
      notes: client.notes || ''
    });
    setEditorOpen(true);
  };

  const saveClient = async () => {
    if (!form.name.trim()) {
      showToast('Client name is required.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        monthlyLimit: numberValue(form.monthlyLimit),
        annualLimit: numberValue(form.annualLimit),
        includedEmailLimit: numberValue(form.includedEmailLimit)
      };
      if (form.id) {
        await gatewayApi.updateClient(payload);
        showToast('Client updated in PocketBase.');
      } else {
        await gatewayApi.createClient(payload);
        showToast('Client created in PocketBase.');
      }
      setEditorOpen(false);
      await loadClients();
    } catch (err) {
      showToast(err.message || 'Unable to save client.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openOverview = async (client) => {
    setOverviewOpen(true);
    setOverview(null);
    setOverviewLoading(true);
    try {
      const response = await gatewayApi.clientOverview(client.id);
      setOverview(response.data);
    } catch (err) {
      showToast(err.message || 'Unable to load client overview.', 'error');
      setOverviewOpen(false);
    } finally {
      setOverviewLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Clients</h2>
          <p className="text-sm text-slate-500">Enter the business once and connect its application automatically. Advanced client editing is still available.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadClients} className="px-3 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50">
            <SafeIcon icon={FiRefreshCw} />
          </button>
          <button onClick={()=>navigate('/guided-setup')} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 shadow-sm flex items-center gap-2"><SafeIcon icon={FiPlus} /> Set up business + app</button>
          <button onClick={openCreate} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-600">Client only (advanced)</button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input
            type="text"
            placeholder="Search clients by name, email, industry or status..."
            className="w-full text-sm outline-none bg-transparent py-1"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full text-left whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100">
                <th className="px-6 py-4">Client</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Monthly Limit</th>
                <th className="px-6 py-4">Included Allowance</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-400">Loading clients from PocketBase...</td></tr>
              ) : filteredClients.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-400">No clients found.</td></tr>
              ) : filteredClients.map(client => (
                <tr key={client.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                        {(client.name || 'C').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">{client.name}</p>
                        <p className="text-xs text-slate-500">{client.industry || 'No industry set'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{client.contactEmail || '—'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${client.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                      {client.status || 'active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-semibold text-slate-700">{Number(client.monthlyLimit || 0).toLocaleString()}</td>
                  <td className="px-6 py-4 font-semibold text-slate-700">{Number(client.includedEmailLimit || 0).toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" onClick={()=>navigate('/guided-setup?client='+encodeURIComponent(client.id))} className="mr-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-2 text-xs font-bold text-blue-700" title="Connect this business without retyping its details">Connect app</button>
                      <button onClick={() => openOverview(client)} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-blue-600 hover:bg-blue-50" title="Client overview">
                        <SafeIcon icon={FiEye} />
                      </button>
                      <button onClick={() => openEdit(client)} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-blue-600 hover:bg-blue-50" title="Edit client">
                        <SafeIcon icon={FiEdit3} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editorOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold">{form.id ? 'Edit Client' : 'Create Client'}</h3>
                <p className="text-xs text-slate-400">Saved securely through the PocketBase gateway runtime.</p>
              </div>
              <button onClick={() => setEditorOpen(false)} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} className="text-xl" /></button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Client Name *" value={form.name} onChange={value => setForm({ ...form, name: value })} />
                <Field label="Contact Email" type="email" value={form.contactEmail} onChange={value => setForm({ ...form, contactEmail: value })} />
                <Field label="Industry" value={form.industry} onChange={value => setForm({ ...form, industry: value })} />
                <SelectField label="Status" value={form.status} onChange={value => setForm({ ...form, status: value })} options={['active', 'paused', 'suspended']} />
              </div>

              <section>
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Quotas</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Monthly Limit" type="number" value={form.monthlyLimit} onChange={value => setForm({ ...form, monthlyLimit: value })} />
                  <Field label="Annual Limit" type="number" value={form.annualLimit} onChange={value => setForm({ ...form, annualLimit: value })} />
                  <Field label="Included Email Allowance" type="number" value={form.includedEmailLimit} onChange={value => setForm({ ...form, includedEmailLimit: value })} />
                </div>
              </section>

              <section>
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Default Sender Identity</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="From Name" value={form.fromName} onChange={value => setForm({ ...form, fromName: value })} />
                  <Field label="From Email" type="email" value={form.fromEmail} onChange={value => setForm({ ...form, fromEmail: value })} />
                  <Field label="Reply-To Email" type="email" value={form.replyToEmail} onChange={value => setForm({ ...form, replyToEmail: value })} />
                </div>
              </section>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Internal Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 w-full h-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500" />
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setEditorOpen(false)} className="px-4 py-2 text-sm font-bold text-slate-500">Cancel</button>
              <button onClick={saveClient} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : 'Save Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {overviewOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex justify-end">
          <div className="bg-white w-full max-w-2xl h-full shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 bg-slate-900 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold">Client Overview</h3>
                <p className="text-xs text-slate-400">Client, apps, keys and email usage in one place.</p>
              </div>
              <button onClick={() => setOverviewOpen(false)} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} className="text-xl" /></button>
            </div>

            {overviewLoading || !overview ? (
              <div className="p-10 text-center text-slate-400">Loading client intelligence...</div>
            ) : (
              <div className="p-6 space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{overview.client.name}</h2>
                  <p className="text-sm text-slate-500">{overview.client.contactEmail || 'No contact email'} • {overview.client.status}</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric icon={FiBox} label="Apps" value={overview.apps.length} />
                  <Metric icon={FiKey} label="API Keys" value={overview.apiKeys.length} />
                  <Metric icon={FiMail} label="Sent This Month" value={Number(overview.usage.monthly || 0).toLocaleString()} />
                  <Metric icon={FiActivity} label="Sent All Time" value={Number(overview.usage.total || 0).toLocaleString()} />
                </div>

                <Panel title="Sender Identity">
                  <Detail label="From Name" value={overview.client.fromName || 'Inherited from gateway'} />
                  <Detail label="From Email" value={overview.client.fromEmail || 'Inherited from gateway'} />
                  <Detail label="Reply-To" value={overview.client.replyToEmail || 'Inherited from gateway'} />
                </Panel>

                <Panel title="Applications">
                  {overview.apps.length ? overview.apps.map(app => (
                    <div key={app.id} className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-sm font-bold text-slate-800">{app.name}</p>
                        <p className="text-xs text-slate-500">{app.origins || 'No origin set'}</p>
                      </div>
                      <span className="text-xs font-bold text-slate-500">{app.status}</span>
                    </div>
                  )) : <p className="text-sm text-slate-400">No applications.</p>}
                </Panel>

                <Panel title="API Keys">
                  {overview.apiKeys.length ? overview.apiKeys.map(key => (
                    <div key={key.id} className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-sm font-bold text-slate-800">{key.name}</p>
                        <code className="text-xs text-slate-500">{key.maskedKey || key.keyPrefix || 'No prefix'}</code>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-slate-700">{key.status}</p>
                        <p className="text-[10px] text-slate-400">{key.validUntil || 'No expiry'}</p>
                      </div>
                    </div>
                  )) : <p className="text-sm text-slate-400">No API keys.</p>}
                </Panel>

                <Panel title="Recent Email Activity">
                  {overview.recentLogs.length ? overview.recentLogs.slice(0, 10).map(log => (
                    <div key={log.id} className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-xs font-bold text-slate-700">{log.subject || 'Email request'}</p>
                        <p className="text-[10px] text-slate-400">{log.recipient || '—'}</p>
                      </div>
                      <span className={`text-[10px] font-bold uppercase ${log.status === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{log.status}</span>
                    </div>
                  )) : <p className="text-sm text-slate-400">No email activity yet.</p>}
                </Panel>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none">
      {options.map(option => <option key={option} value={option}>{option}</option>)}
    </select>
  </div>
);

const Metric = ({ icon, label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
    <SafeIcon icon={icon} className="text-blue-500 mb-2" />
    <p className="text-xl font-bold text-slate-900">{value}</p>
    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</p>
  </div>
);

const Panel = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <h4 className="font-bold text-slate-900 mb-3">{title}</h4>
    {children}
  </div>
);

const Detail = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-xs font-bold text-slate-800 text-right">{value}</span>
  </div>
);

export default Clients;